import React, { useContext, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ImageInputProps } from './types';
import {
  ButtonContainer,
  HiddenInput,
  ImageContainer,
  ImageInputContainer,
  ImageNameContainer,
  ImagePreview,
  NoImageContainer,
  NoImageTextContainer,
} from './styles';
import Button from '../Button/Button';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import Tooltip, { ToolTipPosition } from '../Tooltip/Tooltip';
import { ErrorMessage, HelperMessage, HelperContainer } from '../Input/styles';

export const ImageInput = ({
  input,
  meta,
  helperText,
  canDelete = true,
  orientation = 'square',
  replaceText = 'Reemplazar',
  uploadText = 'Seleccionar archivo',
  formatText = 'Formato png o jpeg',
  testId,
  disabled,
}: ImageInputProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { actualTheme } = useContext(ThemeContext);
  const hasError = meta && meta.invalid && meta.touched;

  const processFile = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      setFileName(file.name);
      reader.onload = () => {
        input.onChange(reader.result);
      };
    }
  };

  const handleUpload = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleClear = () => {
    input.onChange('');
  };

  const handleDragOver = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    if (disabled) return;
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    processFile(file);
  };

  const showHelperOrError = () => {
    if (hasError) {
      return (
        <>
          <ErrorMessage data-testid={`${testId}-error`}>{meta.error}</ErrorMessage>
        </>
      );
    }

    if (helperText)
      return (
        <HelperMessage disabled={disabled} data-testid={`${testId}-help-message`}>
          {helperText}
        </HelperMessage>
      );

    return null;
  };

  return (
    <ThemeProvider theme={actualTheme}>
      <ImageInputContainer hasError={hasError}>
        <ImageContainer
          data-testid={`${testId}-image-container`}
          imageUploaded={!!input.value}
          orientation={orientation}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          isDragging={isDragging}
          onClick={handleUpload}
          disabled={disabled}
        >
          {input.value ? (
            <ImagePreview src={input.value} alt="" />
          ) : (
            <NoImageContainer>
              <BasicIcon
                clickable={!disabled}
                name="upload"
                size="large"
                color={disabled ? '#ccc' : '#ff6c0e'}
              />
              <NoImageTextContainer disabled={disabled}>
                <strong data-testid={`${testId}-upload-text`}>{uploadText}</strong>
                <p data-testid={`${testId}-format-text`}>{formatText}</p>
              </NoImageTextContainer>
            </NoImageContainer>
          )}
        </ImageContainer>
        {input.value && (
          <ButtonContainer>
            <ImageNameContainer>
              <p data-testid={`${testId}-file-name`}>{fileName}</p>
              {canDelete && (
                <Button
                  onClick={handleClear}
                  iconName="trash"
                  hierarchy="secondary"
                  testId={`${testId}-delete-button`}
                />
              )}
            </ImageNameContainer>
            <Tooltip message={replaceText} position={ToolTipPosition.top}>
              <Button
                onClick={handleUpload}
                iconName="switch-vertical"
                hierarchy="secondary"
                testId={`${testId}-replace-button`}
              />
            </Tooltip>
          </ButtonContainer>
        )}
      </ImageInputContainer>
      <HelperContainer>{showHelperOrError()}</HelperContainer>

      <HiddenInput {...input} />
      <HiddenInput
        type="file"
        ref={inputRef}
        onChange={handleImageChange}
        style={{ display: 'none' }}
        accept="image/*"
      />
    </ThemeProvider>
  );
};
